import { useMemo, useState } from 'react';
import { Mail, Trash2, X } from 'lucide-react';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Button } from '../../ui/button';
import { cn } from '../../../lib/cn';
import {
  AZIONI_LISTA,
  DOMANDE,
  PASSI,
  PERCHE_NIENTE_APERTURE,
  type AzioneLista,
  type ConfigAttendiEvento,
  type ConfigAttesa,
  type ConfigCondizione,
  type ConfigEtichetta,
  type ConfigIngresso,
  type ConfigInvio,
  type EmailAutomationStep,
  type TipoDomanda,
} from '../../../types/automazioni.types';
import type { EtichettaScelta } from '../../../lib/email/automationService';
import { ASPETTO_PASSI } from './aspettoPassi';

interface PannelloPassoProps {
  passo: EmailAutomationStep;
  /** Le altre caselle: servono alla domanda, che si riferisce a una email. */
  invii: EmailAutomationStep[];
  etichette: EtichettaScelta[];
  onChange: (passo: EmailAutomationStep) => void;
  onComponi: () => void;
  onElimina: () => void;
  onChiudi: () => void;
}

/** Le tendine hanno lo stesso aspetto dei campi di testo accanto. */
const STILE_TENDINA =
  'w-full mt-1 h-10 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2';

/** Durate proposte. Sono quelle che si usano davvero; il resto si scrive. */
const UNITA = [
  { label: 'minuti', minuti: 1 },
  { label: 'ore', minuti: 60 },
  { label: 'giorni', minuti: 60 * 24 },
];

function scomponi(minuti: number): { quanto: number; unita: number } {
  for (const u of [...UNITA].reverse()) {
    if (minuti >= u.minuti && minuti % u.minuti === 0) {
      return { quanto: minuti / u.minuti, unita: u.minuti };
    }
  }
  return { quanto: minuti || 0, unita: 1 };
}

/**
 * Quanto, in minuti/ore/giorni: un numero e una tendina. L'unita' resta
 * quella scelta anche mentre il numero e' vuoto: svuotare "3 giorni" per
 * scrivere 5 non deve diventare "5 minuti".
 */
function SceltaDurata({ minuti, onChange }: { minuti: number; onChange: (minuti: number) => void }) {
  const [unita, setUnita] = useState(() => scomponi(minuti).unita);
  const quanto = minuti > 0 ? minuti / unita : 0;
  return (
    <div className="flex gap-2 mt-1">
      <Input
        type="number"
        min={1}
        value={quanto || ''}
        onChange={(e) => onChange(Math.max(Number(e.target.value), 0) * unita)}
        className="w-24"
      />
      <select
        value={unita}
        onChange={(e) => {
          const nuova = Number(e.target.value);
          setUnita(nuova);
          onChange(quanto * nuova);
        }}
        className={cn(STILE_TENDINA, 'mt-0 flex-1')}
      >
        {UNITA.map((u) => (
          <option key={u.label} value={u.minuti}>
            {u.label}
          </option>
        ))}
      </select>
    </div>
  );
}

/** La tendina delle liste, uguale in tutte le caselle che ne usano una. */
function SceltaLista({
  valore,
  etichette,
  onChange,
}: {
  valore: string | null | undefined;
  etichette: EtichettaScelta[];
  onChange: (tagId: string | null) => void;
}) {
  return (
    <div>
      <Label className="text-xs">Quale lista</Label>
      <select
        value={valore ?? ''}
        onChange={(e) => onChange(e.target.value || null)}
        className={STILE_TENDINA}
      >
        <option value="">
          {etichette.length === 0 ? 'Nessuna lista: creala nella scheda Liste' : 'Scegli...'}
        </option>
        {etichette.map((t) => (
          <option key={t.id} value={t.id}>
            {t.name}
          </option>
        ))}
      </select>
    </div>
  );
}

/** Quanto aspetta al massimo "aspetta che entri in una lista" se non ne arriva una. */
const MASSIMO_PROPOSTO = 60 * 24 * 3;

/**
 * Il pannello che si apre scegliendo una casella: qui dentro si dice cosa
 * deve fare.
 */
export function PannelloPasso({
  passo,
  invii,
  etichette,
  onChange,
  onComponi,
  onElimina,
  onChiudi,
}: PannelloPassoProps) {
  const descrizione = PASSI[passo.kind];
  const aspetto = ASPETTO_PASSI[passo.kind];
  const Icona = aspetto.icona;

  const minutiAttesa = useMemo(
    () => Number((passo.config as ConfigAttesa).minuti ?? 0),
    [passo.config]
  );
  const massimoAttesa = (passo.config as ConfigAttendiEvento).max_minuti ?? null;

  // Le caselle lista salvate prima del controllo non hanno l'azione: per il
  // motore valgono "metti", e qui si mostra lo stesso.
  const azioneLista: AzioneLista = (passo.config as ConfigEtichetta).azione ?? 'aggiungi';

  const cambiaConfig = (config: Record<string, unknown>) =>
    onChange({ ...passo, config: { ...passo.config, ...config } as EmailAutomationStep['config'] });

  return (
    <aside className="absolute inset-y-0 right-0 z-10 w-full sm:w-[340px] lg:static flex-shrink-0 border-l border-neutral-200 bg-white overflow-y-auto shadow-[-8px_0_24px_rgba(0,0,0,0.06)] lg:shadow-none">
      <div className="flex items-start gap-3 p-4 border-b border-neutral-100">
        <div className={cn('w-10 h-10 flex items-center justify-center flex-shrink-0', aspetto.pieno)}>
          <Icona className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-neutral-900">{descrizione.label}</p>
          <p className="text-xs text-neutral-500 leading-relaxed mt-0.5">{descrizione.spiegazione}</p>
        </div>
        <button
          type="button"
          onClick={onChiudi}
          aria-label="Chiudi il pannello"
          className="w-8 h-8 -mr-1 -mt-1 flex items-center justify-center text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="p-4 space-y-5">
        {passo.kind !== 'fine' && (
          <div>
            <Label htmlFor="nome-passo" className="text-xs">
              Come si chiama
            </Label>
            <Input
              id="nome-passo"
              value={passo.label ?? ''}
              onChange={(e) => onChange({ ...passo, label: e.target.value })}
              placeholder="Serve solo a te, per ritrovarla nel disegno"
              className="mt-1"
            />
          </div>
        )}

        {/* --- La email --- */}
        {passo.kind === 'invio' && (
          <>
            <div>
              <Label htmlFor="oggetto-passo" className="text-xs">
                Oggetto
              </Label>
              <Input
                id="oggetto-passo"
                value={(passo.config as ConfigInvio).subject ?? ''}
                onChange={(e) => cambiaConfig({ subject: e.target.value })}
                placeholder="Quello che si legge nella posta in arrivo"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="preheader-passo" className="text-xs">
                Riga di anteprima
              </Label>
              <Input
                id="preheader-passo"
                value={(passo.config as ConfigInvio).preheader ?? ''}
                onChange={(e) => cambiaConfig({ preheader: e.target.value })}
                placeholder="La frase accanto all'oggetto, prima di aprire"
                className="mt-1"
              />
            </div>
            <Button variant="outline" onClick={onComponi} className="w-full">
              <Mail className="w-4 h-4 mr-2" />
              Componi la email
            </Button>
            <p className="text-[11px] text-neutral-400 leading-relaxed">
              {((passo.config as ConfigInvio).document?.blocchi?.length ?? 0) === 0
                ? 'La email e\' ancora vuota: finche\' lo resta la sequenza non si accende.'
                : `${(passo.config as ConfigInvio).document.blocchi.length} blocchi dentro.`}
            </p>
          </>
        )}

        {/* --- L'attesa --- */}
        {passo.kind === 'attesa' && (
          <div>
            <Label className="text-xs">Quanto aspetta</Label>
            <SceltaDurata
              key={passo.id}
              minuti={minutiAttesa}
              onChange={(minuti) => cambiaConfig({ minuti })}
            />
            <p className="text-[11px] text-neutral-400 mt-2 leading-relaxed">
              Se l'attesa finisce di notte o in un giorno in cui non si spedisce, l'invio slitta
              alla prima ora buona.
            </p>
          </div>
        )}

        {/* --- Aspetta che entri in una lista --- */}
        {passo.kind === 'attendi_evento' && (
          <>
            <SceltaLista
              valore={(passo.config as ConfigAttendiEvento).tag_id}
              etichette={etichette}
              onChange={(tag_id) => cambiaConfig({ tag_id })}
            />
            <p className="text-[11px] text-neutral-400 -mt-3 leading-relaxed">
              Conta solo se il contatto viene messo nella lista dopo essere arrivato qui. Se ce
              l'ha gia', aspetta che venga tolta e rimessa (come fa una riconferma).
            </p>
            <div>
              <label className="flex items-center gap-2 text-xs font-medium text-neutral-700">
                <input
                  type="checkbox"
                  checked={massimoAttesa !== null}
                  onChange={(e) =>
                    cambiaConfig({ max_minuti: e.target.checked ? MASSIMO_PROPOSTO : null })
                  }
                  className="h-4 w-4 rounded border-neutral-300"
                />
                Con un tempo massimo
              </label>
              {massimoAttesa !== null && (
                <SceltaDurata
                  key={passo.id}
                  minuti={Number(massimoAttesa)}
                  onChange={(max_minuti) => cambiaConfig({ max_minuti })}
                />
              )}
              <p className="text-[11px] text-neutral-400 mt-2 leading-relaxed">
                {massimoAttesa !== null
                  ? 'Se la lista non arriva in tempo, il contatto prende l\'uscita "Scaduto" (es. un promemoria). Il tempo conta come nell\'attesa normale: la notte e i giorni senza invii non scadono.'
                  : 'Senza tempo massimo il contatto aspetta finche\' serve: esce solo da "Arrivata".'}
              </p>
            </div>
          </>
        )}

        {/* --- Ogni volta che entra in una lista --- */}
        {passo.kind === 'ingresso_evento' && (
          <>
            <SceltaLista
              valore={(passo.config as ConfigIngresso).tag_id}
              etichette={etichette}
              onChange={(tag_id) => cambiaConfig({ tag_id })}
            />
            <p className="text-[11px] text-neutral-400 leading-relaxed">
              Ogni volta che il contatto viene messo in questa lista, fa le caselle collegate qui
              sotto, poi si ferma. Il suo percorso principale non si muove. Vale per chi e' entrato
              nella sequenza, non per chi ne e' uscito. Per poterla rifare, alla fine toglilo dalla
              lista (casella Lista, "Toglilo dalla lista").
            </p>
          </>
        )}

        {/* --- La domanda --- */}
        {passo.kind === 'condizione' && (
          <>
            <div>
              <Label className="text-xs">Cosa controlla</Label>
              <select
                value={(passo.config as ConfigCondizione).tipo ?? ''}
                onChange={(e) => cambiaConfig({ tipo: e.target.value as TipoDomanda })}
                className={STILE_TENDINA}
              >
                <option value="">Scegli...</option>
                {(Object.keys(DOMANDE) as TipoDomanda[]).map((t) => (
                  <option key={t} value={t}>
                    {DOMANDE[t].label}
                  </option>
                ))}
              </select>
              {(passo.config as ConfigCondizione).tipo && (
                <p className="text-[11px] text-neutral-400 mt-1.5 leading-relaxed">
                  {DOMANDE[(passo.config as ConfigCondizione).tipo].spiegazione}
                </p>
              )}
            </div>

            {['ha_cliccato', 'ha_risposto', 'ha_ricevuto'].includes(
              (passo.config as ConfigCondizione).tipo ?? ''
            ) && (
              <div>
                <Label className="text-xs">Riferita a quale email</Label>
                <select
                  value={(passo.config as ConfigCondizione).step_id ?? ''}
                  onChange={(e) => cambiaConfig({ step_id: e.target.value || null })}
                  className={STILE_TENDINA}
                >
                  <option value="">Una qualsiasi di questa sequenza</option>
                  {invii.map((i) => (
                    <option key={i.id} value={i.id}>
                      {i.label || (i.config as ConfigInvio).subject || 'email senza nome'}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {(passo.config as ConfigCondizione).tipo === 'ha_etichetta' && (
              <SceltaLista
                valore={(passo.config as ConfigCondizione).tag_id}
                etichette={etichette}
                onChange={(tag_id) => cambiaConfig({ tag_id })}
              />
            )}

            {(passo.config as ConfigCondizione).tipo === 'prima_di' && (
              <div>
                <Label htmlFor="data-passo" className="text-xs">
                  Quale giorno
                </Label>
                <Input
                  id="data-passo"
                  type="date"
                  value={(passo.config as ConfigCondizione).data ?? ''}
                  onChange={(e) => cambiaConfig({ data: e.target.value || null })}
                  className="mt-1"
                />
              </div>
            )}

            <p className="text-[11px] text-neutral-400 leading-relaxed border-t border-neutral-100 pt-3">
              {PERCHE_NIENTE_APERTURE}
            </p>
          </>
        )}

        {/* --- La lista --- */}
        {passo.kind === 'etichetta' && (
          <>
            <SceltaLista
              valore={(passo.config as ConfigEtichetta).tag_id}
              etichette={etichette}
              onChange={(tag_id) => cambiaConfig({ tag_id })}
            />
            <div>
              <Label className="text-xs">Cosa fare</Label>
              <select
                value={azioneLista}
                onChange={(e) => cambiaConfig({ azione: e.target.value as AzioneLista })}
                className={STILE_TENDINA}
              >
                {(Object.keys(AZIONI_LISTA) as AzioneLista[]).map((a) => (
                  <option key={a} value={a}>
                    {AZIONI_LISTA[a].label}
                  </option>
                ))}
              </select>
              <p className="text-[11px] text-neutral-400 mt-1.5 leading-relaxed">
                {AZIONI_LISTA[azioneLista].spiegazione}
              </p>
            </div>
          </>
        )}

        <div className="pt-4 border-t border-neutral-100">
          <Button
            variant="ghost"
            onClick={onElimina}
            className="w-full justify-center text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Togli questa casella
          </Button>
        </div>
      </div>
    </aside>
  );
}
